import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Flame, Check, Gift, Truck, PackageCheck, Palette, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import ProductGrid from '../components/ProductGrid';
import { FAQSection, Testimonials } from '../components/FaqTestimonials';
import { PRODUCTS, FAQS } from '../mock/mock';

// Diwali 2026 - November 8, 2026 (as per Hindu calendar)
const DIWALI = new Date('2026-11-08T00:00:00+05:30').getTime();

const useCountdown = () => {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const diff = DIWALI - Date.now();
      if (diff <= 0) return setT({ d: 0, h: 0, m: 0, s: 0 });
      const d = Math.floor(diff / (1000 * 60 * 60 * 24));
      const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const m = Math.floor((diff / (1000 * 60)) % 60);
      const s = Math.floor((diff / 1000) % 60);
      setT({ d, h, m, s });
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, []);
  return t;
};

const CountdownBox = ({ n, label }) => (
  <div className="bg-white/95 backdrop-blur rounded-xl md:rounded-2xl px-3 py-3 md:px-6 md:py-5 min-w-[70px] md:min-w-[110px] text-center shadow-xl">
    <div className="font-serif-display text-3xl md:text-5xl text-[#2C2416] leading-none">{String(n).padStart(2, '0')}</div>
    <div className="text-[10px] md:text-xs uppercase tracking-widest text-[#7A5C4C] mt-1 md:mt-2">{label}</div>
  </div>
);

const PRICING_TIERS = [
  {
    name: 'Essential',
    range: '25 - 99 hampers',
    price: 899,
    tag: '',
    highlights: ['Ready-to-ship curated hampers', 'Standard gift wrap & ribbon', 'Personalised gift note card', 'Delivery within 10-14 days'],
    color: 'pastel-sage',
  },
  {
    name: 'Signature',
    range: '100 - 499 hampers',
    price: 1499,
    tag: 'Most Popular',
    highlights: ['Semi-custom hamper design', 'Logo printing on box + card', 'Curated 6-8 premium products', 'Dedicated account manager', 'PAN India delivery in 7-10 days'],
    color: 'pastel-blush',
    featured: true,
  },
  {
    name: 'Luxury',
    range: '500+ hampers',
    price: 2999,
    tag: 'Bespoke',
    highlights: ['Fully bespoke hamper design', 'Custom-made packaging & inserts', 'Premium 8-12 curated products', 'Custom branding & inserts', 'Priority delivery in 5-7 days', 'Post-delivery unboxing report'],
    color: 'pastel-peach',
  },
];

const DIWALI_PRODUCTS = [
  { id: 'd1', name: 'The Golden Diya Box', category: 'Diwali Signature', price: 1890, img: 'https://images.unsplash.com/photo-1625552186152-668cd2f0b707', img2: 'https://images.pexels.com/photos/20699855/pexels-photo-20699855.jpeg', tag: 'Best Seller' },
  { id: 'd2', name: 'Sweet Blessings Hamper', category: 'Diwali', price: 960, img: 'https://images.unsplash.com/photo-1668127494486-f27a1d2b88f9', img2: 'https://images.unsplash.com/photo-1608755728617-aefab37d2edd' },
  { id: 'd3', name: 'Mini Treasures Diwali', category: 'Diwali', price: 1340, img: 'https://images.unsplash.com/photo-1694481901573-a970f982ac5e', img2: 'https://images.unsplash.com/photo-1625552187571-7ee60ac43d2b' },
  { id: 'd4', name: 'Dry Fruit Palette', category: 'Diwali Premium', price: 2000, img: 'https://images.pexels.com/photos/17878238/pexels-photo-17878238.jpeg', img2: 'https://images.pexels.com/photos/20699855/pexels-photo-20699855.jpeg', tag: 'Premium' },
  { id: 'd5', name: 'Celebration Feltbag', category: 'Diwali', price: 1960, img: 'https://images.unsplash.com/photo-1571915096107-c6606c216058', img2: 'https://images.unsplash.com/photo-1512909006721-3d6018887383' },
  { id: 'd6', name: 'The Choti Diwali Set', category: 'Diwali', price: 2460, img: 'https://images.pexels.com/photos/14755150/pexels-photo-14755150.jpeg', img2: 'https://images.unsplash.com/photo-1668127494486-f27a1d2b88f9' },
  { id: 'd7', name: 'Rajwada Royale Hamper', category: 'Diwali Luxury', price: 4610, img: 'https://images.unsplash.com/photo-1608755727748-dfa2e44f255b', img2: 'https://images.unsplash.com/photo-1674620213535-9b2a2553ef40', tag: 'Luxury' },
  { id: 'd8', name: 'Roshni Executive Box', category: 'Diwali Executive', price: 3870, img: 'https://images.unsplash.com/photo-1513201099705-a9746e1e201f', img2: 'https://images.unsplash.com/photo-1592903297149-37fb25202dfa' },
];

const DiwaliHero = () => {
  const t = useCountdown();
  return (
    <section className="relative overflow-hidden text-white">
      <div className="absolute inset-0">
        <img src="https://images.unsplash.com/photo-1625552186152-668cd2f0b707" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#2C1810]/85 via-[#3A2418]/70 to-[#2C1810]/95" />
      </div>

      {/* Twinkling dots */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 40 }).map((_, i) => (
          <span key={i} className="absolute w-1 h-1 rounded-full bg-[#F4D5C5]"
            style={{
              left: `${(i * 37) % 100}%`,
              top: `${(i * 53) % 100}%`,
              opacity: 0.4 + ((i % 5) * 0.12),
              animation: `pulse 2.4s ${i * 0.1}s infinite`,
            }} />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-32 text-center">
        <div className="inline-flex items-center gap-2 bg-[#C9A76B]/25 border border-[#C9A76B]/50 text-[#F4D5C5] px-4 py-1.5 rounded-full text-xs uppercase tracking-widest backdrop-blur">
          <Flame className="w-3.5 h-3.5" /> Diwali 2026 • Bookings Open
        </div>
        <h1 className="font-serif-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl mt-6 leading-[1.02]">
          Diwali,<br /><span className="text-[#F4D5C5]">lit with love.</span>
        </h1>
        <p className="max-w-2xl mx-auto mt-6 text-lg text-white/80 leading-relaxed">
          Curated festive hampers with your brand, delivered PAN India. Lock in your Diwali order now for the best pricing and preferred stock.
        </p>

        <div className="mt-10 flex flex-wrap items-end justify-center gap-2 md:gap-4">
          <CountdownBox n={t.d} label="Days" />
          <span className="font-serif-display text-3xl text-[#F4D5C5] pb-3 hidden md:inline">:</span>
          <CountdownBox n={t.h} label="Hours" />
          <span className="font-serif-display text-3xl text-[#F4D5C5] pb-3 hidden md:inline">:</span>
          <CountdownBox n={t.m} label="Minutes" />
          <span className="font-serif-display text-3xl text-[#F4D5C5] pb-3 hidden md:inline">:</span>
          <CountdownBox n={t.s} label="Seconds" />
        </div>
        <p className="mt-4 text-xs uppercase tracking-widest text-white/60">Until Diwali • 8 November 2026</p>

        <div className="flex flex-wrap gap-3 justify-center mt-10">
          <a href="#collection" className="bg-[#F4D5C5] text-[#2C2416] rounded-full px-7 py-3.5 text-sm uppercase tracking-widest font-medium hover:bg-white transition-colors inline-flex items-center gap-2">
            View Diwali hampers <ArrowRight className="w-4 h-4" />
          </a>
          <a href="#diwali-enquire" className="border border-white/40 rounded-full px-7 py-3.5 text-sm uppercase tracking-widest hover:bg-white/10 transition-colors">
            Book a Consultation
          </a>
        </div>
      </div>

      <style>{`@keyframes pulse{0%,100%{opacity:.2;transform:scale(1)}50%{opacity:.9;transform:scale(1.4)}}`}</style>
    </section>
  );
};

const PricingSection = () => (
  <section id="pricing" className="py-16 md:py-24 bg-[#FAF6F1]">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <div className="text-xs uppercase tracking-[0.24em] text-[#C58B76] mb-3">Bulk Pricing Tiers</div>
        <h2 className="font-serif-display text-3xl md:text-5xl text-[#2C2416] leading-tight">The more you gift, the more you save</h2>
        <p className="text-[#5B4A3A] mt-4">Transparent per-hamper pricing across three tiers. Every tier includes GST invoicing and PAN India delivery.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {PRICING_TIERS.map((t) => (
          <div key={t.name}
            className={`relative rounded-3xl p-8 border ${t.featured ? 'border-[#2C2416] bg-white shadow-2xl md:-mt-4 md:mb-4' : 'border-[#EFE3D2] bg-white'} transition-transform hover:-translate-y-1`}>
            {t.tag && (
              <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] uppercase tracking-widest ${t.featured ? 'bg-[#2C2416] text-[#F4D5C5]' : 'bg-[#F4D5C5] text-[#2C2416]'}`}>{t.tag}</div>
            )}
            <div className={`w-14 h-14 rounded-full ${t.color} flex items-center justify-center mb-5`}>
              <Gift className="w-6 h-6 text-[#2C2416]" />
            </div>
            <h3 className="font-serif-display text-3xl text-[#2C2416]">{t.name}</h3>
            <div className="text-xs uppercase tracking-widest text-[#7A5C4C] mt-1">{t.range}</div>
            <div className="mt-6 flex items-baseline gap-1">
              <span className="text-sm text-[#7A5C4C]">Starting at</span>
            </div>
            <div className="font-serif-display text-5xl text-[#2C2416] leading-none">
              ₹{t.price.toLocaleString('en-IN')}
              <span className="text-base font-normal text-[#7A5C4C] ml-1">/hamper</span>
            </div>
            <ul className="mt-6 space-y-3">
              {t.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#2C2416]">
                  <Check className="w-4 h-4 mt-0.5 text-[#C58B76] flex-shrink-0" /> {h}
                </li>
              ))}
            </ul>
            <a href="#diwali-enquire" className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full py-3 text-xs uppercase tracking-widest transition-colors ${t.featured ? 'btn-primary' : 'btn-outline'}`}>
              Get quote <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        ))}
      </div>

      <div className="mt-10 text-center text-xs text-[#7A5C4C]">
        * Volume discounts of up to 22% available for orders above 1,000 hampers. GST @18% applicable.
      </div>
    </div>
  </section>
);

const WhyDiwali = () => (
  <section className="py-16 md:py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-12">
        <h2 className="font-serif-display text-3xl md:text-4xl text-[#2C2416]">Everything you need for a stress-free Diwali</h2>
      </div>
      <div className="grid md:grid-cols-4 gap-4">
        {[
          { icon: Palette, title: 'Design Studio', desc: 'Our stylists design hampers around your brand and story' },
          { icon: PackageCheck, title: 'Kitting & QC', desc: 'Every hamper hand-packed and quality checked before dispatch' },
          { icon: Truck, title: 'PAN India', desc: 'Shipped to every pincode, real-time tracking included' },
          { icon: Sparkles, title: 'Wow Unboxing', desc: 'Insta-worthy packaging with your logo, ribbon and card' },
        ].map((i, k) => (
          <div key={k} className="bg-[#FAF6F1] rounded-2xl p-6 border border-[#EFE3D2]">
            <div className="w-12 h-12 rounded-full pastel-blush flex items-center justify-center mb-4">
              <i.icon className="w-5 h-5 text-[#7A5C4C]" />
            </div>
            <h4 className="font-serif-display text-xl text-[#2C2416]">{i.title}</h4>
            <p className="text-sm text-[#5B4A3A] mt-1">{i.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Diwali = () => {
  return (
    <main>
      <SEO
        title="Corporate Diwali Gifts for Employees & Clients 2026 | WrapYourWish"
        description="Book corporate Diwali gifts for employees and clients. Luxury dry fruit boxes, artisanal diyas, gourmet sweets & curated hampers with custom branding and GST billing."
        keywords="corporate Diwali gifts for employees, corporate Diwali gifts, festive hampers corporate, Diwali gift hampers bulk, corporate New Year gifts India"
        canonicalUrl="https://wrapyourwish.com/diwali"
        ogImage="https://images.unsplash.com/photo-1625552186152-668cd2f0b707"
      />
      <div className="bg-[#2C2416] text-[#F4D5C5] text-center py-2 text-xs uppercase tracking-widest">
        <Link to="/" className="inline-flex items-center gap-1 hover:text-white">
          <ChevronRight className="w-3 h-3 rotate-180" /> Back to Home
        </Link>
      </div>
      <DiwaliHero />
      <WhyDiwali />
      <ProductGrid title="Diwali Hamper Collection" subtitle="Curated picks from our 2026 festive edit" products={DIWALI_PRODUCTS} />
      <Testimonials />
      <FAQSection />
      <div id="diwali-enquire">
        <ContactForm source="diwali-2026" />
      </div>
    </main>
  );
};

export default Diwali;
